import { useState, useEffect, useMemo } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { MapPin, Users, Home, BarChart3, RefreshCw, X } from "lucide-react";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { formatCurrency } from "@/lib/demo-data";

type ViewMode = "ratio" | "leads" | "pros";

interface DeptData {
  code: string;
  leadsCount: number;
  prosActiveCount: number;
  unlockedRate: number;
  avgBudget: number;
}

const DEPT_NAMES: Record<string, string> = {
  "01":"Ain","02":"Aisne","03":"Allier","04":"Alpes-de-Haute-Provence","05":"Hautes-Alpes",
  "06":"Alpes-Maritimes","07":"Ardeche","08":"Ardennes","09":"Ariege","10":"Aube",
  "11":"Aude","12":"Aveyron","13":"Bouches-du-Rhone","14":"Calvados","15":"Cantal",
  "16":"Charente","17":"Charente-Maritime","18":"Cher","19":"Correze","2A":"Corse-du-Sud",
  "2B":"Haute-Corse","21":"Cote-d'Or","22":"Cotes-d'Armor","23":"Creuse","24":"Dordogne",
  "25":"Doubs","26":"Drome","27":"Eure","28":"Eure-et-Loir","29":"Finistere",
  "30":"Gard","31":"Haute-Garonne","32":"Gers","33":"Gironde","34":"Herault",
  "35":"Ille-et-Vilaine","36":"Indre","37":"Indre-et-Loire","38":"Isere","39":"Jura",
  "40":"Landes","41":"Loir-et-Cher","42":"Loire","43":"Haute-Loire","44":"Loire-Atlantique",
  "45":"Loiret","46":"Lot","47":"Lot-et-Garonne","48":"Lozere","49":"Maine-et-Loire",
  "50":"Manche","51":"Marne","52":"Haute-Marne","53":"Mayenne","54":"Meurthe-et-Moselle",
  "55":"Meuse","56":"Morbihan","57":"Moselle","58":"Nievre","59":"Nord",
  "60":"Oise","61":"Orne","62":"Pas-de-Calais","63":"Puy-de-Dome","64":"Pyrenees-Atlantiques",
  "65":"Hautes-Pyrenees","66":"Pyrenees-Orientales","67":"Bas-Rhin","68":"Haut-Rhin","69":"Rhone",
  "70":"Haute-Saone","71":"Saone-et-Loire","72":"Sarthe","73":"Savoie","74":"Haute-Savoie",
  "75":"Paris","76":"Seine-Maritime","77":"Seine-et-Marne","78":"Yvelines","79":"Deux-Sevres",
  "80":"Somme","81":"Tarn","82":"Tarn-et-Garonne","83":"Var","84":"Vaucluse",
  "85":"Vendee","86":"Vienne","87":"Haute-Vienne","88":"Vosges","89":"Yonne",
  "90":"Territoire de Belfort","91":"Essonne","92":"Hauts-de-Seine","93":"Seine-Saint-Denis",
  "94":"Val-de-Marne","95":"Val-d'Oise","971":"Guadeloupe","972":"Martinique",
  "973":"Guyane","974":"Reunion","976":"Mayotte",
};

function getRatio(d: DeptData): number {
  if (d.prosActiveCount === 0) return d.leadsCount > 0 ? 999 : 0;
  return d.leadsCount / d.prosActiveCount;
}

function getRatioColor(ratio: number): string {
  if (ratio === 0) return "bg-gray-100 text-gray-500";
  if (ratio > 5) return "bg-red-100 text-red-700";
  if (ratio > 2) return "bg-orange-100 text-orange-700";
  if (ratio >= 0.5) return "bg-green-100 text-green-700";
  return "bg-blue-100 text-blue-700";
}

function getRatioLabel(ratio: number): string {
  if (ratio === 0) return "Pas de donnees";
  if (ratio > 5) return "Manque de pros";
  if (ratio > 2) return "Desequilibre";
  if (ratio >= 0.5) return "Equilibre";
  return "Sur-acquisition";
}

function getValueForView(d: DeptData, view: ViewMode): number {
  if (view === "leads") return d.leadsCount;
  if (view === "pros") return d.prosActiveCount;
  return getRatio(d);
}

function sortDepts(data: DeptData[], view: ViewMode, ascending: boolean): DeptData[] {
  return [...data].sort((a, b) => {
    const va = getValueForView(a, view);
    const vb = getValueForView(b, view);
    return ascending ? vb - va : va - vb;
  });
}

export default function MarketplaceGeo() {
  const [viewMode, setViewMode] = useState<ViewMode>("ratio");
  const [since, setSince] = useState("30d");
  const [selectedDept, setSelectedDept] = useState<DeptData | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const utils = trpc.useUtils();

  const { data, isLoading, error } = trpc.admin.analytics.getGeoConcentration.useQuery(
    { since: since as "7d" | "30d" | "90d" },
    { refetchInterval: 300_000 }
  );

  const sorted = useMemo(() => {
    if (!data?.departements) return [];
    return sortDepts(data.departements, viewMode, true);
  }, [data, viewMode]);

  if (isLoading) {
    return <AdminLayout><ListPageSkeleton /></AdminLayout>;
  }

  if (error) {
    return (
      <AdminLayout>
        <EmptyState
          icon={<MapPin className="h-8 w-8" />}
          title="Erreur de chargement"
          description={error.message}
          action={{ label: "Reessayer", onClick: () => utils.admin.analytics.getGeoConcentration.invalidate() }}
        />
      </AdminLayout>
    );
  }

  const totalLeads = sorted.reduce((s, d) => s + d.leadsCount, 0);
  const totalPros = sorted.reduce((s, d) => s + d.prosActiveCount, 0);
  const avgUnlockRate = sorted.length > 0
    ? Math.round(sorted.reduce((s, d) => s + d.unlockedRate, 0) / sorted.length)
    : 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Densite Geographique</h1>
            <p className="text-muted-foreground text-sm">
              Repartition offre/demande par departement — {sorted.length} departements avec activite
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => utils.admin.analytics.getGeoConcentration.invalidate()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraichir
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Home className="h-4 w-4" /> Leads total
              </div>
              <div className="text-2xl font-bold">{totalLeads}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Users className="h-4 w-4" /> Pros actifs
              </div>
              <div className="text-2xl font-bold">{totalPros}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <BarChart3 className="h-4 w-4" /> Taux unlock moyen
              </div>
              <div className="text-2xl font-bold">{avgUnlockRate}%</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <MapPin className="h-4 w-4" /> Departements
              </div>
              <div className="text-2xl font-bold">{sorted.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <div className="flex gap-3 items-center flex-wrap">
          <div className="flex gap-1 rounded-lg border p-1">
            {(["ratio", "leads", "pros"] as const).map((mode) => (
              <Button
                key={mode}
                variant={viewMode === mode ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode(mode)}
              >
                {mode === "ratio" ? "Ratio match" : mode === "leads" ? "Leads" : "Pros"}
              </Button>
            ))}
          </div>
          <Select value={since} onValueChange={setSince}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 jours</SelectItem>
              <SelectItem value="30d">30 jours</SelectItem>
              <SelectItem value="90d">90 jours</SelectItem>
            </SelectContent>
          </Select>

          {/* Legend */}
          <div className="flex gap-2 ml-auto text-xs">
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-400 inline-block" /> Manque de pros</span>
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-400 inline-block" /> Desequilibre</span>
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-green-400 inline-block" /> Equilibre</span>
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-blue-400 inline-block" /> Sur-acquisition</span>
          </div>
        </div>

        {/* Table view (always shown — mobile fallback + desktop companion) */}
        <div className="flex gap-6">
          <Card className="flex-1">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dept</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead className="text-right">Leads</TableHead>
                    <TableHead className="text-right">Pros</TableHead>
                    <TableHead className="text-right">Ratio</TableHead>
                    <TableHead className="text-right">Unlock %</TableHead>
                    <TableHead className="text-right">Budget moy.</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((d) => {
                    const ratio = getRatio(d);
                    const colorCls = getRatioColor(ratio);
                    return (
                      <TableRow
                        key={d.code}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedDept(d)}
                      >
                        <TableCell className="font-mono font-medium">{d.code}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{DEPT_NAMES[d.code] ?? d.code}</TableCell>
                        <TableCell className="text-right">{d.leadsCount}</TableCell>
                        <TableCell className="text-right">{d.prosActiveCount}</TableCell>
                        <TableCell className="text-right font-medium">
                          {ratio === 999 ? "∞" : ratio.toFixed(1)}
                        </TableCell>
                        <TableCell className="text-right">{d.unlockedRate}%</TableCell>
                        <TableCell className="text-right">{d.avgBudget > 0 ? formatCurrency(d.avgBudget) : "-"}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={colorCls}>
                            {getRatioLabel(ratio)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Side panel */}
          {selectedDept && !isMobile && (
            <Card className="w-80 shrink-0 self-start sticky top-8">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {selectedDept.code} — {DEPT_NAMES[selectedDept.code] ?? selectedDept.code}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDept(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-muted-foreground">Leads</div>
                    <div className="text-xl font-bold">{selectedDept.leadsCount}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Pros actifs</div>
                    <div className="text-xl font-bold">{selectedDept.prosActiveCount}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Taux unlock</div>
                    <div className="text-xl font-bold">{selectedDept.unlockedRate}%</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Budget moyen</div>
                    <div className="text-xl font-bold">
                      {selectedDept.avgBudget > 0 ? formatCurrency(selectedDept.avgBudget) : "-"}
                    </div>
                  </div>
                </div>
                <div>
                  <Badge
                    variant="secondary"
                    className={getRatioColor(getRatio(selectedDept))}
                  >
                    {getRatioLabel(getRatio(selectedDept))}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground pt-2 border-t">
                  Ratio leads/pros : {getRatio(selectedDept) === 999 ? "∞" : getRatio(selectedDept).toFixed(1)}
                  {getRatio(selectedDept) > 5 && " — zone prioritaire pour acquisition pro"}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
